import {ChangeEvent, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch} from 'react-redux';

import {getUserTimezoneApi} from 'redux/api/user';
import {updateUserTimezoneAction} from 'redux/auth/authAsyncActions';
import {getBrowserTimezone, getTimezoneOptions} from 'utils/helpers/timezone';

// UCSF-3519: state of the Timezone field on the profile page. '' is "Automatic" (null on the server);
// savedTimezone is the last value read or saved, so only a real change enables Save.
export const useProfileTimezone = (): {
  timezone: string;
  timezoneLoaded: boolean;
  isTimezoneChanged: boolean;
  timezoneOptions: { id: string; name: string }[];
  handleChangeTimezone: (e: ChangeEvent<HTMLSelectElement>) => void;
  saveTimezone: () => void;
} => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [timezone, setTimezone] = useState('');
  const [savedTimezone, setSavedTimezone] = useState('');
  const [timezoneLoaded, setTimezoneLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    getUserTimezoneApi()
      .then(({data}) => {
        if (active) {
          setTimezone(data?.timezone || '');
          setSavedTimezone(data?.timezone || '');
          setTimezoneLoaded(true);
        }
      })
      .catch((e) => {
        console.error('getUserTimezoneApi error', {message: e?.message});
      });

    return () => {
      active = false;
    };
  }, []);

  const timezoneOptions = useMemo(() => {
    const browserTimezone = getBrowserTimezone();

    return [
      {
        id: '',
        name: intl.formatMessage({
          id: browserTimezone ? 'gritx.user-settings.timezone.automaticDetected' : 'gritx.user-settings.timezone.automatic',
          defaultMessage: browserTimezone ? 'Automatic ({zone})' : 'Automatic'
        }, {zone: browserTimezone})
      },
      ...getTimezoneOptions(savedTimezone || null).map(({id, name}) => ({id, name}))
    ];
  }, [savedTimezone, intl]);

  function handleChangeTimezone(e: ChangeEvent<HTMLSelectElement>) {
    setTimezone(e.target.value);
  }

  function saveTimezone() {
    const value = timezone;

    (dispatch(updateUserTimezoneAction(value || null)) as unknown as Promise<boolean>)
      .then((saved) => {
        if (saved) {
          setSavedTimezone(value);
        }
      });
  }

  return {
    timezone,
    timezoneLoaded,
    isTimezoneChanged: timezoneLoaded && timezone !== savedTimezone,
    timezoneOptions,
    handleChangeTimezone,
    saveTimezone
  };
};
